import { type ReactNode } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  AreaFilter,
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters'
import { AREA_CODE_OPTIONS } from '~/models/area'
import { SearchInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/SearchInput'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'

type Props = {
  selectedAreaCode: AreaCode
  onSelectArea: (a: AreaCode) => void
  searchText: string
  onSearchTextChange: (s: string) => void
  /** 検索対象がタブごとに異なるため、 placeholder は呼び出し側から渡す。 */
  searchPlaceholder: string
  /**
   * エリアの右・検索の左に差し込む追加フィルタ。
   * design/admin/occto.html の `.ecl-toolbar` は タブ固有のフィルタも
   * エリア → (ここ) → 検索 の順で 1 行に並べるため、 別行に切り出さずここに差し込む
   * (現在の利用者は発電販売計画タブの計画区分のみ)。
   */
  afterArea?: ReactNode
  /**
   * 検索欄の右隣に並べる追加スロット (例: 失効表示トグル + 非表示件数)。
   * design/admin/occto.html の `.ecl-toolbar` は area + search + 失効 checkbox
   * + 件数 span を単一 flex 行に並べる (`gap: 16px 20px`) ため、 別行に切り出さず
   * ここに差し込む。
   */
  trailing?: ReactNode
}

/**
 * 広域マスタ非事業者タブ共通の「エリア + 検索」フィルタ行 (spec `occto-master/index.md`
 * AC-002)。 業務エリア設定 (個人設定) に含まれるエリアのみを選択肢に絞り込む
 * (「すべて」は常に表示)。 個人設定が未設定・取得中・取得エラーの場合は全エリアに
 * fallback する (`~/pages/SupplyGap/index.tsx` の既存前例に合わせる)。
 */
export const MasterFilterToolbar = ({
  selectedAreaCode,
  onSelectArea,
  searchText,
  onSearchTextChange,
  searchPlaceholder,
  afterArea,
  trailing,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data } = useGetPersonalSettingsQuery()
  const businessAreas = data?.settings?.businessAreas ?? []
  const areaOptions =
    businessAreas.length > 0
      ? AREA_CODE_OPTIONS.filter((code) => businessAreas.includes(code))
      : AREA_CODE_OPTIONS

  return (
    <FilterToolbar>
      {/* エリア選択。 design (`design/admin/occto.html` #occto-area-group) は
          ラジオで「すべて」+ 10 エリアの 11 択、 既定「すべて」。 共通 AreaFilter
          (radio) を使い、 AreaCode.UNSPECIFIED を「すべて」の sentinel として
          undefined に橋渡しする。 */}
      <FilterSection label={t('page:adminOcctoMaster.toolbar.area')}>
        <AreaFilter
          allLabel={t('page:adminOcctoMaster.toolbar.areaAll')}
          areaCodes={areaOptions}
          onChange={(area) => onSelectArea(area ?? AreaCode.UNSPECIFIED)}
          value={
            selectedAreaCode === AreaCode.UNSPECIFIED
              ? undefined
              : selectedAreaCode
          }
          variant="radio"
        />
      </FilterSection>
      {afterArea}
      {/* 検索 input
       *
       * **検索テキストは AC-SC-031 (フィルタ選択状態の localStorage 保持) の対象に
       * 含めない。** `shared-components.md` 機能詳細 5 が「フィルタバーを持つ画面は
       * AC-SC-031 により保持を必須とする (画面個別の spec で改めて規定しない)」と
       * するため、 除外の判断はここに残す。
       *
       * - AC-SC-031 が復元を求めるのは「フィルタの**選択状態**」で、 同 §6 の
       *   AC-SC-019〜021 と機能詳細 2 はそれをボタングループの単一選択 / 複数選択と
       *   定義している。 自由入力テキストを扱う AC は §6 に無い
       * - 広域マスタは「タブ切り替え時にフィルタ条件をリセット」
       *   (`admin/master/occto-master/index.md` 機能詳細 1) を、 **選択中タブのみを
       *   マウントする構造**で満たしている (`AdminOcctoMaster/index.tsx` の
       *   `ActiveTabComponent`)。 mount 時に復元するとタブ往復で検索語が戻り、
       *   この規定を破る
       * - 検索で 0 件になった時、 この画面は `common:table.empty`「データがありません」
       *   しか出せない (`useMasterCrudTab` の `emptyLabel` は失効非表示で全滅した時
       *   のみ設定)。 復元語がヒットしないと**マスタが空だと誤認させる**
       *
       * 将来保持するなら (1) タブを次元に含む保存キー (2) 検索起因の 0 件を区別する
       * 空状態、 の 2 点を同時に入れること。 なお同じ理由でエリア / 失効トグルも
       * 素直には保持できない (この画面全体の AC-SC-031 対応方針は issue 参照)。
       */}
      <FilterSection label={t('page:adminOcctoMaster.toolbar.searchLabel')}>
        <SearchInput
          onChange={onSearchTextChange}
          placeholder={searchPlaceholder}
          value={searchText}
        />
      </FilterSection>
      {trailing}
    </FilterToolbar>
  )
}
